// src/pages/ProfileSettings.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { auth, db } from '../firebase/config';
import { updateProfile, updateEmail } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

const ProfileSettings: React.FC = () => {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const loadUserData = useCallback(async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // If firstName and lastName exist, use them
        // Otherwise, try to split displayName if it exists
        if (userData.firstName && userData.lastName) {
          setFirstName(userData.firstName);
          setLastName(userData.lastName);
        } else if (userData.displayName) {
          // Try to split existing displayName
          const nameParts = userData.displayName.trim().split(/\s+/);
          setFirstName(nameParts[0] || '');
          setLastName(nameParts.slice(1).join(' ') || '');
        }
        
        setEmail(userData.email || user.email || '');
        setProfilePictureUrl(userData.profilePictureUrl || user.photoURL || '');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setProfilePictureUrl(user.photoURL || '');
      
      // Load user data from Firestore
      loadUserData();
    }
  }, [user, loadUserData]);


  // Upload profile picture to Bluehost server
  const uploadProfilePicture = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientId', user?.uid || 'unknown');
    formData.append('type', 'profile-picture');

    const response = await fetch('https://helpkeepmymoney.com/upload.php', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload profile picture');
    }

    const result = await response.json();
    return result.fileUrl;
  };

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setStatus('Error: Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setStatus('Error: Image must be less than 5MB');
        return;
      }

      setProfilePicture(file);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setStatus('');

    try {
      if (user) {
        let photoURL = user.photoURL;

        // Upload profile picture if selected
        if (profilePicture) {
          try {
            photoURL = await uploadProfilePicture(profilePicture);
            setProfilePictureUrl(photoURL);
          } catch (uploadError) {
            setStatus(`Error uploading image: ${uploadError}`);
            setIsUploading(false);
            return;
          }
        }

        // Combine firstName and lastName for displayName
        const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();

        // Update Firebase Auth profile
        await updateProfile(user, { 
          displayName: displayName || undefined,
          photoURL: photoURL || undefined
        });
        
        if (email !== user.email) {
          await updateEmail(user, email);
        }

        // Save user data to Firestore
        // Get existing user data to preserve role if it exists
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        const existingData = userDoc.exists() ? userDoc.data() : {};
        
        await setDoc(userDocRef, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName, // Store combined name for backwards compatibility
          email,
          profilePictureUrl: photoURL || '',
          updatedAt: new Date(),
          uid: user.uid,
          role: existingData.role || 'client', // Default to client if role doesn't exist
          createdAt: existingData.createdAt || new Date() // Preserve creation date
        }, { merge: true });

        setStatus('Profile updated successfully');
        setProfilePicture(null); // Clear the file input
      }
    } catch (err: any) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ textAlign: 'center', margin: '2rem' }}>
        <p>Please sign in to access your profile.</p>
        <button onClick={() => navigate('/sign-in')}>Go to Sign In</button>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: '500px', margin: '2rem auto', padding: '20px' }}>
        <h2>Profile Settings</h2>
      
      {/* Profile Picture Section */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <div style={{ 
          width: '120px', 
          height: '120px', 
          borderRadius: '50%', 
          overflow: 'hidden', 
          margin: '0 auto 15px',
          border: '3px solid #007bff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          {profilePictureUrl ? (
            <img 
              src={profilePictureUrl} 
              alt="Profile" 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: '48px', color: '#6c757d' }}>👤</span>
          )}
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>
            Profile Picture
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePictureChange}
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            Max size: 5MB. Supported formats: JPG, PNG, GIF
          </small>
        </div>
      </div>

      <form onSubmit={handleUpdate}>
        <div style={{ marginBottom: '15px' }}>
          <label>First Name:</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Last Name:</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <button 
          type="submit"
          disabled={isUploading}
          style={{ 
            backgroundColor: isUploading ? '#ccc' : '#007bff', 
            color: 'white', 
            padding: '10px 20px', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: isUploading ? 'not-allowed' : 'pointer',
            width: '100%'
          }}
        >
          {isUploading ? 'Updating...' : 'Update Profile'}
        </button>
      </form>

      {status && (
        <p style={{ 
          color: status.includes('Error') ? 'red' : 'green', 
          textAlign: 'center', 
          marginTop: '15px' 
        }}>
          {status}
        </p>
      )}
      </div>
    </div>
  );
};

export default ProfileSettings;
